// https://github.com/vercel/swr/blob/master/examples/axios-typescript/libs/useRequest.ts
import { useEffect } from "react";

import { axiosApiInstance } from "API";
import { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import useSWR, { SWRConfiguration, SWRResponse, mutate as swrMutate } from "swr";
import { getApiPath } from "utils";

// SWR by default fires all hooks at once on window focus which freezes the UI.
// Instead we disable built-in focus revalidation and stagger the revalidation manually.
const activeKeys = new Set<string>();
const STAGGER_MS = 50;

window.addEventListener("focus", () => {
  [...activeKeys].forEach((key, i) => {
    setTimeout(() => swrMutate(key), i * STAGGER_MS);
  });
});

export type GetRequest = AxiosRequestConfig | null;

interface Return<Data, Error>
  extends Pick<SWRResponse<AxiosResponse<Data>, AxiosError<Error>>, "isValidating" | "error" | "mutate"> {
  data: Data | undefined;
  response: AxiosResponse<Data> | undefined;
  clearCache: () => void;
}

export interface Config<Data = unknown, Error = unknown>
  extends Omit<SWRConfiguration<AxiosResponse<Data>, AxiosError<Error>>, "fallbackData"> {
  fallbackData?: Data;
}

export default function useRequest<Data = unknown, Error = unknown>(
  request: GetRequest,
  { fallbackData, ...config }: Config<Data, Error> = {}
): Return<Data, Error> {
  if (request) {
    request.url = getApiPath(request.url ?? "");
    request.withCredentials = true;
  }
  const key = request && JSON.stringify(request);

  // respect explicit revalidateOnFocus: false
  const shouldRevalidateOnFocus = config.revalidateOnFocus !== false;
  useEffect(() => {
    if (!key || !shouldRevalidateOnFocus) return;
    activeKeys.add(key);
    return () => {
      activeKeys.delete(key);
    };
  }, [key, shouldRevalidateOnFocus]);

  const {
    data: response,
    error,
    isValidating,
    mutate,
  } = useSWR<AxiosResponse<Data>, AxiosError<Error>>(
    key,
    /**
     * NOTE: Typescript thinks `request` can be `null` here, but the fetcher
     * function is actually only called by `useSWR` when it isn't.
     */
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => axiosApiInstance.request<Data>(request!),
    {
      revalidateOnFocus: false, // handled above
      ...config,
      fallbackData: fallbackData && {
        status: 200,
        statusText: "InitialData",
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        config: request!,
        headers: {},
        data: fallbackData,
      },
    }
  );

  return {
    data: response && response.data,
    response,
    error,
    isValidating,
    mutate,
    clearCache: () => swrMutate(key, null, true),
  };
}
